import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Formata uma data para string YYYY-MM-DD usando componentes locais.
 * IMPORTANTE: Evita toISOString() que converte para UTC e causa shift de data.
 */
export function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Formata uma data para string ISO com timezone (para created_at).
 * Inclui offset de timezone para evitar conversão incorreta pelo banco.
 */
export function formatLocalDateTime(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  // Incluir offset de timezone para PostgreSQL interpretar corretamente
  const tzOffset = -date.getTimezoneOffset();
  const tzSign = tzOffset >= 0 ? '+' : '-';
  const tzHours = String(Math.floor(Math.abs(tzOffset) / 60)).padStart(2, '0');
  const tzMins = String(Math.abs(tzOffset) % 60).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${tzSign}${tzHours}:${tzMins}`;
}

/**
 * Retorna a data de hoje formatada como YYYY-MM-DD (local).
 */
export function getTodayLocalDate(): string {
  return formatLocalDate(new Date());
}

/**
 * Parse uma string YYYY-MM-DD como data local (NÃO UTC).
 * IMPORTANTE: new Date('YYYY-MM-DD') interpreta como UTC, causando shift de data.
 * Esta função evita esse problema parseando os componentes manualmente.
 */
export function parseDateString(dateStr: string): Date {
  if (!dateStr) return new Date();
  const [year, month, day] = dateStr.split('-').map(Number);
  // Month é 0-indexed
  return new Date(year, month - 1, day);
}

/**
 * Converte uma string de data YYYY-MM-DD para formato com timezone local.
 * IMPORTANTE: Usar esta função ao enviar datas de inputs type="date" para o banco.
 * Evita o problema de -1 dia causado por interpretação UTC.
 */
export function formatDateForDB(dateStr: string): string {
  if (!dateStr) return '';
  // Parse como data local e formatar com timezone
  const date = parseDateString(dateStr.split('T')[0]);
  return formatLocalDateTime(date);
}
